import {
  Dropdown as NextUIDropdown,
  DropdownItem as NextUIDropdownItem,
  DropdownItemProps as NextUIDropdownItemProps,
  DropdownMenu as NextUIDropdownMenu,
  DropdownTrigger as NextUIDropdownTrigger,
} from "@heroui/react";
import clsx from "clsx";
import { TRACK_EVENTS } from "core/consts";
import { TourElement } from "core/types";
import { Button } from "ds/ui";
import { NewChip } from "dsl/atoms/SettingsNavigation";
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  LucideProps,
} from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { matchPath, useLocation } from "react-router-dom";
import { useTracking } from "react-tracking";

export type DropdownNavbarMenuItem = Pick<
  NextUIDropdownItemProps,
  "description" | "onPress" | "href"
> & {
  hrefMatch?: string;
  icon?: React.ReactElement<LucideProps>;
  id: string;
  label: string;
  showNewChip?: boolean;
  tour?: TourElement;
};

export type DropdownNavbarProps = {
  id: string;
  items: DropdownNavbarMenuItem[];
  trackingEventName: keyof typeof TRACK_EVENTS;
};

function useActiveDropdownItem({ items }: { items: DropdownNavbarMenuItem[] }) {
  const location = useLocation();

  return useMemo(() => {
    const matches = items
      .map((item) => ({
        item,
        match: item.hrefMatch
          ? matchPath(item.hrefMatch, location.pathname)
          : false,
      }))
      .filter(({ match }) => match !== null && match !== false);

    // Rank the matches by the length of the hrefMatch string
    matches.sort((a, b) => {
      const aLength = a.item.hrefMatch?.length || 0;
      const bLength = b.item.hrefMatch?.length || 0;
      return aLength - bLength;
    });

    // Return the item with the longest matching pattern (most specific)
    return matches.length > 0 ? matches[matches.length - 1].item : null;
  }, [items, location.pathname]);
}

export function DropdownNavbar({
  id,
  items,
  trackingEventName,
}: DropdownNavbarProps) {
  const { trackEvent } = useTracking();
  const activeItem = useActiveDropdownItem({ items });
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  return (
    <NextUIDropdown
      classNames={{
        base: "before:shadow-md w-full",
        trigger: "w-full desktop:w-[256px] justify-between",
      }}
      // The popover exit animation can fail to complete here, leaving the menu
      // visually open after selecting an item or clicking outside (POW-355).
      // Disabling the animation makes the menu close deterministically.
      disableAnimation
      // Controlling the open state makes every close path deterministic
      // (item select, outside click, Escape, and toggling via the trigger
      // caret) instead of relying on HeroUI's internal uncontrolled state.
      isOpen={isOpen}
      // Clicks on the trigger must not count as "interact outside": otherwise
      // pressing the caret while open fires the outside-dismiss AND the
      // trigger toggle, which reopens the menu and leaves it stuck open
      // (POW-355). Excluding the trigger leaves only the toggle, so it closes.
      shouldCloseOnInteractOutside={(element) =>
        !triggerRef.current?.contains(element)
      }
      onOpenChange={setIsOpen}
    >
      <NextUIDropdownTrigger>
        <Button
          ref={triggerRef}
          className={clsx(
            "uppercase",
            "focus:ring-0 aria-expanded:scale-100 data-[pressed=true]:scale-[1]",
          )}
          color="primary"
          disableRipple
          endContent={
            <>
              <ChevronUpIcon
                aria-hidden={true}
                className="hidden group-aria-expanded:block"
              />
              <ChevronDownIcon
                aria-hidden={true}
                className="group-aria-expanded:hidden"
              />
            </>
          }
          id={id}
          size="sm"
          variant="faded"
        >
          <span>{activeItem?.label ?? ""}</span>
        </Button>
      </NextUIDropdownTrigger>
      <NextUIDropdownMenu
        classNames={{
          base: "w-full max-h-[45vh] tablet:max-h-[302px] overflow-y-auto outline-hidden",
          list: "gap-1 w-[87.5vw] tablet:w-[92.5vw] desktop:w-full",
        }}
        color="primary"
        selectionMode="none"
      >
        {items.map(
          ({ description, href, id: itemId, label, onPress, showNewChip }) => {
            const isCurrentPage = itemId === activeItem?.id;

            return (
              <NextUIDropdownItem
                key={itemId}
                aria-current={isCurrentPage ? "page" : undefined}
                className={clsx(
                  "group rounded-lg bg-white! p-2 ring-1",
                  "data-[focus=true]:bg-primary-light! data-[focus=true]:ring-primary data-[focus=true]:outline-1! data-[focus=true]:outline-offset-1! data-[focus=true]:outline-solid!",
                  "data-[hover=true]:bg-primary-light! data-[hover=true]:text-gray-900 data-[hover=true]:ring-primary data-[hover=true]:outline-1! data-[hover=true]:outline-offset-1! data-[hover=true]:outline-solid!",
                  {
                    "bg-primary-light! ring-primary": isCurrentPage,
                    "ring-transparent": !isCurrentPage,
                  },
                )}
                endContent={isCurrentPage ? <CheckIcon size={16} /> : undefined}
                href={href}
                id={itemId}
                textValue={label}
                onPress={(e) => {
                  trackEvent({
                    item: itemId,
                    name: TRACK_EVENTS[trackingEventName],
                  });
                  setIsOpen(false);
                  requestAnimationFrame(() => onPress?.(e));
                }}
              >
                <div className="flex justify-between">
                  <h2 className="text-sm font-medium text-gray-900 uppercase">
                    {label}
                  </h2>
                  {showNewChip && <NewChip />}
                </div>
                {description && (
                  <p className="text-xs font-normal text-gray-600">
                    {description}
                  </p>
                )}
              </NextUIDropdownItem>
            );
          },
        )}
      </NextUIDropdownMenu>
    </NextUIDropdown>
  );
}
